"use client"

import type React from "react"
import { useCookies } from "next-client-cookies"
import { useTranslations } from "next-intl"
import { usePathname } from "next/navigation"
import LanguageSwitcher from "@/components/i18n/language-switcher"
import { NavHeader } from "@/components/sidebar/nav-header"
import { NavMain } from "@/components/sidebar/nav-main"
import { NavUser } from "@/components/sidebar/nav-user"
import { SidebarHeader } from "@/components/sidebar/sidebar-header"
import SidebarUnits from "@/components/sidebar/sidebar-units"
import { Sidebar, SidebarContent, SidebarFooter, SidebarProvider, SidebarRail } from "@/components/ui/sidebar"
import { ChannelSyncProvider } from "@/hooks/use-channel-sync-context"
import { cn } from "@/lib/utils"
import { env } from "@/lib/env"

type MainLayoutProps = {
  children: React.ReactNode
}

export function MainLayout({ children }: MainLayoutProps): React.JSX.Element {
  const t = useTranslations()
  const cookies = useCookies()
  const defaultOpen = cookies.get("sidebar_state") === "true" || false
  const layoutSettings = {
    mode: "sidebar" as const,
    position: "left" as "left" | "right",
    collapsible: "icon" as const,
    variant: "inset" as const,
  }
  const pathname = usePathname()
  const isInSimpleDataEntry = pathname.includes("simple-data-entry")
  const isInDataEntry = pathname.includes("data-entry") && !isInSimpleDataEntry
  const isInDisciplines = pathname.includes("disciplines")
  const isInStartList = pathname.includes("start-list")
  const isInIntegrations = pathname.includes("integrations")

  // Check if we're in BDM or TTE disciplines
  const isInBDM = pathname.includes("/disciplines/BDM")
  const isInTTE = pathname.includes("/disciplines/TTE")
  const championship = env.NEXT_PUBLIC_CHAMPIONSHIP || "CIS"
  const allowedChampionships = ['CIS']
  const isAllowedChampionship = allowedChampionships.includes(championship)

  // Check if we're in data entry for BDM or TTE (hide sidebar and navbar in this case)
  const isInDataEntryBDM = isInBDM && isInDataEntry
  const isInDataEntryTTE = isInTTE && isInDataEntry
  const hideSidebarForDataEntry = isInDataEntryBDM || isInDataEntryTTE
  const hideNavbarForDataEntry = isInDataEntryBDM || isInDataEntryTTE

  const showUnitsMenu = (isInDataEntry || isInIntegrations || isInSimpleDataEntry) || (isInDisciplines && isInStartList)

  const sidebarType = (isInDataEntry || isInIntegrations || isInSimpleDataEntry) ? "data-entry" : isInStartList ? "start-list" : "main"

  // If we're in data entry for BDM or TTE, show content without sidebar but still provide SidebarProvider
  if (hideSidebarForDataEntry && isAllowedChampionship) {
    return (
      <div className="flex h-screen overflow-hidden">
        <ChannelSyncProvider>
          <SidebarProvider
            defaultOpen={false}
            style={
              {
                "--sidebar-width": "0px",
              } as React.CSSProperties
            }
          >
            {/* No Sidebar component rendered for BDM/TTE */}

            {/* Contenedor principal sin sidebar */}
            <div className="flex h-full min-w-0 flex-1 flex-col">
              {/* Only show NavHeader if not in data entry for BDM/TTE */}
              {!hideNavbarForDataEntry && isAllowedChampionship && (
                <NavHeader position={layoutSettings.position} variant={layoutSettings.variant} />
              )}

              {/* Área de contenido con scroll controlado */}
              <div className="flex-1 overflow-hidden">
                <div className="h-full overflow-auto bg-background">
                  <div className="h-full p-4">{children}</div>
                </div>
              </div>

              {/* Footer */}
              <footer className="flex max-h-8 shrink-0 items-center justify-between border-t bg-background px-4 text-xs text-muted-foreground">
                <div>
                  {t("generic.version", {
                    version: env.APP_VERSION,
                    hash: env.COMMIT_HASH,
                  })}
                </div>
                <div>{t("generic.support_email")}</div>
              </footer>
            </div>
          </SidebarProvider>
        </ChannelSyncProvider>
      </div>
    )
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <ChannelSyncProvider>
        <SidebarProvider
          defaultOpen={defaultOpen}
          style={
            {
              "--sidebar-width": "350px",
            } as React.CSSProperties
          }
        >
          <Sidebar
            className="overflow-hidden rounded-md *:data-[sidebar=sidebar]:flex-row"
            collapsible="icon"
            variant="inset"
          >
            <Sidebar
              className={cn(showUnitsMenu && "w-[calc(var(--sidebar-width-icon)+1px)]! border-r")}
              collapsible={showUnitsMenu ? "none" : layoutSettings.collapsible}
              side={layoutSettings.position}
              variant={layoutSettings.variant}
            >
              <SidebarHeader collapse={showUnitsMenu} />

              <SidebarContent>
                <NavMain collapse={showUnitsMenu} />
              </SidebarContent>

              <SidebarFooter>
                <LanguageSwitcher />
                <NavUser collapse={showUnitsMenu} />
              </SidebarFooter>
              <SidebarRail />
            </Sidebar>
            {showUnitsMenu && <SidebarUnits sidebarType={sidebarType} />}
          </Sidebar>

          {/* Contenedor principal que respeta el sidebar */}
          <div className="m-1 flex h-full min-w-0 flex-1 flex-col rounded-md pb-2">
            <NavHeader position={layoutSettings.position} variant={layoutSettings.variant} />

            {/* Área de contenido con scroll controlado */}
            <div className="flex-1 overflow-hidden">
              <div className="h-full overflow-auto bg-background">
                <div className="h-full p-4">{children}</div>
              </div>
            </div>

            {/* Footer */}
            <footer className="flex max-h-8 shrink-0 items-center justify-between rounded-b-md border-t bg-background px-4 text-xs text-muted-foreground">
              <div>
                {t("generic.version", {
                  version: env.APP_VERSION,
                  hash: env.COMMIT_HASH,
                })}
              </div>
              <div>{t("generic.support_email")}</div>
            </footer>
          </div>
        </SidebarProvider>
      </ChannelSyncProvider>
    </div>
  )
}
